import React, { useState, useCallback, useContext, useMemo, Fragment } from 'react'
import { useForm, useFormContext, useFieldArray, FormProvider, Controller } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { SortableContext } from '@dnd-kit/sortable'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { offset, flip, shift } from '@floating-ui/react-dom-interactions'
import { yupResolver } from '@hookform/resolvers/yup'
import _ from 'lodash'
import { v4 } from 'uuid'
import cx from 'classnames'
import Select from 'react-select'

// assets
import Settings from 'Images/bot-flow-settings.svg'

// contexts & store
import ToggleSwitch from 'Src/components/Toggle'
import Popover from 'Src/b/components/ReactPopover/ReactPopover'
import { deleteAction, addAction, updateAction, setFlowHistory } from 'Actions/flow'

// others
import Draggable from '../../Draggable'
import Step from '../../templates/action-select/Step'
import { StepContext, useFlowContext } from 'Src/containers/contexts'
import { findgoToStep } from '../../../utils/findGoToStep'
import cmeFormValidationSchema from './cme-form-validation-schema'
import EditFieldInfo, { getPayload } from './EditFieldInfo'

const ADD_NEW_FIELD_OPTIONS = [
  { value: 'text_input', label: 'Text Input', hidden: false, disabled: false },
  { value: 'text_area', label: 'Text Area', hidden: false, disabled: false },
  { value: 'dropdown', label: 'Dropdown', hidden: false, disabled: false },
  { value: 'attachments', label: 'Attachment', hidden: false, disabled: false }
]

const SelectCustomField = (props) => {
  // props
  const { applyNewTheme, item, hasError, fieldPath, updateField, isPopupOpen } = props
  // form
  const { control } = useFormContext()

  const update = (option) => {
    if (!updateField) {
      return
    }

    const field = {
      ...item.field,
      type: option.value,
      name: option.label,
      label: option.label
    }
    if (option.value === 'dropdown') {
      field.options = []
    } else if (option.value === 'attachments') {
      field.maxFileCount = 1
      field.validations = {
        ...field.validations,
        allowedTypeCategories: ['Documents', 'Images', 'Audio / Video']
      }
    }
    updateField(field)
  }

  return (
    <Fragment>
      <Controller
        shouldUnregister
        control={control}
        name={`${fieldPath}.name`}
        render={({ field: { value, onChange } }) => {
          const handleChange = (option) => {
            onChange(option.label)
            update(option)
          }

          return (
            <Select
              className={cx('collect-info-select', { 'new-design': applyNewTheme }, [
                hasError ? 'is-invalid' : '',
                isPopupOpen ? 'active' : ''
              ])}
              isDisabled={isPopupOpen}
              menuPosition='fixed'
              classNamePrefix={applyNewTheme ? 'new-design action' : 'action'}
              options={ADD_NEW_FIELD_OPTIONS.filter((value) => !value.hidden)}
              menuPortalTarget={document.body}
              isOptionDisabled={(option) => option.disabled}
              value={ADD_NEW_FIELD_OPTIONS.find((i) => i.label === value)}
              onChange={(option) => handleChange(option)}
            />
          )
        }}
      />
    </Fragment>
  )
}

const ToggleCustomField = (props) => {
  const { item, fieldPath, hasError, isPopupOpen } = props
  // form
  const { control } = useFormContext()

  return (
    <Fragment>
      <div
        className={cx('form-control input-text', [hasError ? 'is-invalid' : '', isPopupOpen ? 'active' : ''])}
        title={_.upperFirst(item.field.label)}
      >
        {_.upperFirst(item.field.label)}
      </div>
      <Controller
        name={`${fieldPath}.enabled`}
        control={control}
        render={({ field: { value, onChange } }) => {
          return (
            <ToggleSwitch
              className={cx('toggle-input', [isPopupOpen ? 'disabled' : ''])}
              checked={value}
              onClick={() => onChange(!value)}
            />
          )
        }}
      />
    </Fragment>
  )
}

const Field = (props) => {
  // props
  const { isNew, item, index, errors, applyNewTheme, update, selectedFieldId } = props

  const fieldPath = `collectInfo.${index}.field`
  const collectInfoErrors = errors.collectInfo ?? null
  const isPopupOpen = selectedFieldId && selectedFieldId === item.id

  const hasError = useMemo(() => {
    if (!collectInfoErrors || !collectInfoErrors.length) {
      return false
    }

    return errors.collectInfo.some((e, i) => e && i === index)
  }, [collectInfoErrors, errors.collectInfo, index])

  const updateField = (updatedField) => {
    update(index, { ...item, field: { ...updatedField } })
  }

  if (isNew) {
    return (
      <SelectCustomField
        item={item}
        fieldPath={fieldPath}
        isPopupOpen={isPopupOpen}
        applyNewTheme={applyNewTheme}
        hasError={hasError}
        updateField={updateField}
      />
    )
  }

  return <ToggleCustomField item={item} isPopupOpen={isPopupOpen} hasError={hasError} fieldPath={fieldPath} />
}

const FieldContainer = (props) => {
  const {
    item,
    index,
    errors,
    fields,
    applyNewTheme,
    selectedFieldId,
    setSelectedFieldId,
    dragHandleProps,
    remove,
    update
  } = props
  // form
  const { clearErrors } = useFormContext()

  const highlightActiveField = selectedFieldId && selectedFieldId === item.id
  const isPopupOpen = !selectedFieldId || selectedFieldId === item.id

  const handleDelete = useCallback(
    (index) => {
      remove(index)
      setSelectedFieldId(null)
    },
    [remove, setSelectedFieldId]
  )

  const handleUpdate = useCallback(
    (data, index) => {
      update(index, { ...data })
      setSelectedFieldId(null)
      clearErrors()
    },
    [clearErrors, setSelectedFieldId, update]
  )

  const handleCancel = useCallback(() => {
    setSelectedFieldId(null)
  }, [setSelectedFieldId])

  const onOpenSettings = useCallback(
    (e, selectedItem) => {
      setSelectedFieldId(selectedItem.id)
    },
    [setSelectedFieldId]
  )

  return (
    <div className={cx('form-group ci-input-group', [!isPopupOpen ? 'disabled' : ''])} key={item.id}>
      <div className='input-toggle-group' {...dragHandleProps}>
        <Field
          item={item}
          index={index}
          errors={errors}
          isNew={item?.isNew}
          applyNewTheme={applyNewTheme}
          selectedFieldId={selectedFieldId}
          update={update}
        />
      </div>
      {!isPopupOpen && (
        // placeholder for disabled state
        <div className='icon'>
          <Settings
            id='editEmail'
            className={cx('settings-icon', [!isPopupOpen ? 'disabled' : ''])}
            height='13'
            width='13'
          />
        </div>
      )}
      {isPopupOpen && (
        <Popover
          placement='right'
          middleware={[offset(30), flip(), shift()]}
          render={(props) => (
            <EditFieldInfo
              {...props}
              item={item}
              index={index}
              collectInfo={fields}
              fieldErrors={errors}
              applyNewTheme={applyNewTheme}
              onDeleteField={handleDelete}
              onUpdateField={handleUpdate}
              onCancelField={handleCancel}
            />
          )}
        >
          <div className='icon'>
            <Settings
              id='editEmail'
              className={cx('settings-icon', [!isPopupOpen ? 'disabled' : '', highlightActiveField ? 'active' : ''])}
              height='13'
              width='13'
              onClick={(e) => onOpenSettings(e, item)}
            />
          </div>
        </Popover>
      )}
    </div>
  )
}

const CollectInfoContent = (props) => {
  // props
  const { errors, applyNewTheme } = props
  // form
  const { control } = useFormContext()
  const { fields, append, remove, update, move } = useFieldArray({
    control,
    name: 'collectInfo'
  })
  // state
  const [selectedFieldId, setSelectedFieldId] = useState(null)

  const onAddAnotherField = useCallback(() => {
    append({
      field: {
        name: 'Text Input',
        type: 'text_input',
        label: 'Text Input',
        helpText: null,
        linkedTo: null,
        validations: {
          required: false
        },
        enabled: true
      },
      isNew: true,
      deletable: true,
      disabled: false
    })
  }, [append])

  const handleDragEnd = (event) => {
    const { over, active } = event

    if (!over) {
      return
    }

    const sourceIndex = fields.findIndex((item) => item.id === active.id)
    const destinationIndex = fields.findIndex((item) => item.id === over.id)

    move(sourceIndex, destinationIndex)
  }

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      }
    })
  )

  return (
    <div className='collect-information-container'>
      <div className='label'>Collect Information</div>
      {errors.collectInfo && (
        <small style={{ marginBottom: '0.5rem' }} className='form-text text-danger'>
          {errors.collectInfo.message}
        </small>
      )}
      <DndContext onDragEnd={handleDragEnd} sensors={sensors} modifiers={[restrictToVerticalAxis]}>
        <SortableContext items={fields} type='COLLECT_INFORMATION'>
          {fields.map((item, index) => (
            <Draggable drag='true' id={item.id} index={index} key={item.id}>
              {(provided) => (
                <FieldContainer
                  item={item}
                  index={index}
                  errors={errors}
                  fields={fields}
                  applyNewTheme={applyNewTheme}
                  selectedFieldId={selectedFieldId}
                  dragHandleProps={provided.dragHandleProps}
                  setSelectedFieldId={setSelectedFieldId}
                  remove={remove}
                  update={update}
                />
              )}
            </Draggable>
          ))}
        </SortableContext>
      </DndContext>
      <div className='add-another-field' onClick={onAddAnotherField}>
        Add another field
      </div>
    </div>
  )
}

const getCollectInfoDefaultValues = (actionSetting) => {
  if (_.isEmpty(actionSetting) || _.isEmpty(actionSetting.fields)) {
    return [
      {
        field: {
          name: 'Name',
          type: 'name',
          label: 'Name',
          helpText: null,
          linkedTo: 'name',
          validations: {
            required: true
          },
          enabled: true
        },
        id: v4(),
        deletable: false,
        disabled: false
      },
      {
        field: {
          name: 'Email',
          type: 'email',
          label: 'Email',
          helpText: null,
          linkedTo: 'email',
          validations: {
            required: true
          },
          enabled: true
        },
        id: v4(),
        deletable: false,
        disabled: false
      }
    ]
  }

  const list = _.cloneDeep(actionSetting.fields) ?? []
  return list.map((item, index) => {
    return {
      id: v4(),
      field: { ...item, linkedTo: item?.linkedTo?.replace('visitor.', '') },
      // TODO: can be moved to child? - on hold
      deletable: !(_.lowerCase(item.name) === 'name' || _.lowerCase(item.name) === 'email'),
      disabled: false
    }
  })
}

const CollectMultiInformationEdit = (props) => {
  // props
  const { mode, actionId, disableOptions, defaultActionType, cancelFn = _.noop } = props
  // context & store
  const dispatch = useDispatch()
  const { step, stepId, row } = useContext(StepContext)
  const { isNewUI } = useFlowContext()
  // variables
  const applyNewTheme = isNewUI
  const settings = _.get(step, 'settings', {})
  const actionSetting = _.get(settings, `${actionId}`, {})
  // state
  const [state] = useState({
    type: _.cloneDeep(actionSetting.type || defaultActionType),
    action: { ...actionSetting.action },
    collectInfo: [...getCollectInfoDefaultValues(actionSetting)]
  })

  const methods = useForm({
    defaultValues: { ...state },
    mode: 'onBlur',
    reValidateMode: 'onChange',
    shouldUnregister: false,
    resolver: yupResolver(cmeFormValidationSchema)
  })
  const {
    handleSubmit,
    formState: { errors, isValid }
  } = methods

  const updateCollectInfo = useCallback(
    (data) => {
      dispatch(
        updateAction({
          ...data,
          actionId
        })
      )

      dispatch(setFlowHistory({ goToStep: findgoToStep(data.setting.action), row }))
    },
    [dispatch, actionId, row]
  )

  const createCollectInfo = useCallback(
    (data) => {
      dispatch(
        addAction({
          ...data,
          actionId: v4()
        })
      )
      dispatch(setFlowHistory({ goToStep: findgoToStep(data.setting.action), row }))
    },
    [dispatch, row]
  )

  const onDeleteClick = useCallback(() => {
    if (!actionId) {
      cancelFn()
      return
    }

    dispatch(
      deleteAction({
        stepId,
        actionId
      })
    )
    dispatch(setFlowHistory({ goToStep: stepId, row: row - 1 }))
  }, [actionId, dispatch, stepId, row, cancelFn])

  const onSubmit = useCallback(
    (data) => {
      if (!_.isEmpty(errors)) {
        return
      }

      const payload = getPayload({ ...data }, stepId)
      if (mode === 'create') {
        createCollectInfo(_.cloneDeep(payload))
      } else {
        updateCollectInfo(_.cloneDeep(payload))
      }

      cancelFn()
    },
    [cancelFn, createCollectInfo, errors, mode, stepId, updateCollectInfo]
  )

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CollectInfoContent
          state={state}
          stepId={stepId}
          errors={errors}
          isValid={isValid}
          applyNewTheme={applyNewTheme}
        />
        <FormProvider {...methods}>
          <div className='form-group'>
            <Step className={cx({ 'new-design': applyNewTheme })} allowStepCreate />
            {errors.action && <small className='form-text text-danger'>{errors.action.message}</small>}
          </div>
        </FormProvider>
        {!disableOptions && (
          <div className='options'>
            <button className='button-primary small save' type='submit'>
              Save
            </button>
            <button className='btn cancel' onClick={cancelFn} type='button'>
              Cancel
            </button>
            <button className='btn delete' onClick={onDeleteClick} type='button'>
              Delete
            </button>
          </div>
        )}
      </form>
    </FormProvider>
  )
}

export default CollectMultiInformationEdit
